// QR Kiosk v2 — KV2-7 note composer: the "Add a note…" row (designer lines 109-114,
// photos only — NO mic glyph, OQ-3) + the shared KioskSheet (Round-6: portaled +
// keyboard-safe, so Cancel/Pridať-poznámku ride above the keyboard while typing).
// Renders iff "note" ∈ allowedActions (gated by the caller, §9.5). Tokens only; no MUI/dt/hex.
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { ServiceReportFileTypeCode } from "../../models/serviceReportsModels";
import { useKioskFiles } from "../reports/useKioskFiles";
import { Glyph, GD } from "../shared/glyphs";
import { ErrorCallout } from "../shared/ErrorCallout";
import { KioskSheet } from "../shared/KioskSheet";
import { KioskTaskDetail } from "./kioskTasksModels";
import { filesToPhotos } from "./taskUtils";
import { addKioskTaskNote, clearActionError, clearNoteCreated, getKioskTask } from "./kioskTasksSlice";
import { TaskPhotoTiles } from "./TaskBits";

const MAX_PHOTOS = 3;

export interface TaskNoteComposerProps {
  tk: KioskTaskDetail;
  number: string;
}

export function TaskNoteComposer({ tk, number }: TaskNoteComposerProps) {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");
  const { translate: tCommon } = useTranslation("common");
  const acting = useAppSelector((s) => s.kioskTasks.acting);
  const actionError = useAppSelector((s) => s.kioskTasks.actionError);
  const noteCreated = useAppSelector((s) => s.kioskTasks.noteCreated);

  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const { files, addFiles, removeFile, rejected, resetFiles } = useKioskFiles(
    ServiceReportFileTypeCode.reportDocument,
    undefined,
    { maxFiles: MAX_PHOTOS, imagesOnly: true }
  );

  const submitting = acting === "noteSubmit";
  const canSubmit = text.trim().length > 0 || files.length > 0;

  // Auto-dismiss the "Note added" confirmation after ~4s.
  useEffect(() => {
    if (!noteCreated) return;
    const timer = setTimeout(() => dispatch(clearNoteCreated()), 4000);
    return () => clearTimeout(timer);
  }, [noteCreated, dispatch]);

  const close = () => {
    if (submitting) return; // in-flight guard blocks close (committed pattern)
    setOpen(false);
    dispatch(clearActionError());
  };

  const submit = async () => {
    if (!canSubmit) return;
    const res = await dispatch(
      addKioskTaskNote({ id: tk.id, number, text: text.trim(), photos: filesToPhotos(files) })
    );
    if (addKioskTaskNote.fulfilled.match(res)) {
      setOpen(false);
      setText("");
      resetFiles([]);
      dispatch(getKioskTask({ number })); // refresh files[]/meta — cheap, keeps the detail truthful
    }
    // rejected → sheet stays open; the slice actionError drives the in-sheet callout below.
  };

  const noteError = actionError && actionError.action === "note";

  return (
    <div style={{ margin: "12px 14px 0", flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 10, height: 50,
          padding: "0 13px", borderRadius: 12, border: "1px solid var(--border)",
          background: "var(--card)", cursor: "pointer", fontFamily: "inherit",
        }}
      >
        <span style={{ flex: 1, textAlign: "left", fontSize: 15, color: "var(--muted)" }}>
          {translate("kioskV2TaskAddNote")}
        </span>
        <span style={{ color: "var(--tier-accent)", display: "inline-flex" }}>
          <Glyph d={GD.cam} size={19} />
        </span>
      </button>

      {noteCreated && (
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "color-mix(in oklab, var(--green) 78%, var(--ink))", marginTop: 6, letterSpacing: "0.03em" }}>
          {translate("kioskV2TaskNoteAdded")}
        </div>
      )}

      <KioskSheet open={open} onRequestClose={close} title={translate("kioskV2TaskNoteTitle")}>
          <textarea
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={2000}
            placeholder={translate("kioskV2TaskNotePlaceholder")}
            style={{
              width: "100%", marginTop: 12, minHeight: 88, borderRadius: 11,
              border: "1px solid var(--border)", background: "var(--bg)", padding: "11px 13px",
              fontSize: 15.5, fontFamily: "inherit", color: "var(--ink)", outline: "none",
              resize: "none", lineHeight: 1.5, boxSizing: "border-box", colorScheme: "light",
            }}
          />

          <div style={{ marginTop: 12 }}>
            <TaskPhotoTiles
              files={files}
              onAdd={addFiles}
              onRemove={removeFile}
              rejectedName={rejected?.name ?? null}
              inputId="task-note-photo"
              max={MAX_PHOTOS}
            />
          </div>

          {noteError && (
            <div style={{ marginTop: 12 }}>
              <ErrorCallout
                title={actionError!.status === 429 ? translate("kioskV2TaskTooMany") : translate("kioskV2TaskActionFailed")}
                hint={actionError!.status === 429 ? undefined : actionError!.message}
              />
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
            <button
              type="button"
              disabled={submitting}
              onClick={close}
              style={{
                flex: 1, height: 48, borderRadius: 11, border: "1px solid var(--border)",
                background: "var(--bg)", color: "var(--ink-2)", fontFamily: "inherit",
                fontSize: 14.5, fontWeight: 600, cursor: "pointer", opacity: submitting ? 0.6 : 1,
              }}
            >
              {tCommon("buttons.cancel")}
            </button>
            <button
              type="button"
              disabled={submitting || !canSubmit}
              onClick={submit}
              style={{
                flex: 1, height: 48, borderRadius: 11, border: "none",
                background: "var(--tier-accent)", color: "var(--tier-accent-fg)", fontFamily: "inherit",
                fontSize: 14.5, fontWeight: 700, cursor: "pointer", opacity: submitting || !canSubmit ? 0.6 : 1,
              }}
            >
              {translate("kioskV2TaskNoteTitle")}
            </button>
          </div>
      </KioskSheet>
    </div>
  );
}

export default TaskNoteComposer;
